import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Loader2, AlertTriangle, CheckCircle2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { deriveBrandColors } from '../../utils/brandColors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const colors = deriveBrandColors();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/team/invite/${token}`)
      .then((r) => {
        if (!cancelled) setInvite(r.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const d = err?.response?.data?.detail;
          setError(typeof d === 'string' ? d : 'Invitation is invalid or no longer available.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Enter your full name');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/team/invite/${token}/accept`, {
        name: name.trim(),
        password,
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Welcome to ${res.data.organization.name}!`);
      window.location.href = '/';
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading invitation…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: `linear-gradient(160deg, ${colors.dark}, ${colors.accent})` }}
      >
        <div
          data-testid="invite-error"
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold text-slate-800">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <Button
            onClick={() => navigate('/auth')}
            className="mt-6"
            style={{ backgroundColor: colors.dark, color: '#fff' }}
          >
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="invite-accept-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: `linear-gradient(160deg, ${colors.dark}, ${colors.accent})` }}
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: colors.accent, color: '#fff' }}
          >
            <Users className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              You've been invited
            </div>
            <h1 className="text-lg font-bold text-slate-800 leading-tight">
              Join <span style={{ color: colors.dark }}>{invite.organization_name}</span>
            </h1>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Crown className="w-4 h-4 text-amber-500" />
            <span>
              <strong className="text-slate-800">{invite.invited_by_name}</strong> invited{' '}
              <strong className="text-slate-800">{invite.email}</strong> as{' '}
              <span
                className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 border border-teal-200 ml-1"
              >
                Staff
              </span>
            </span>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            License: {invite.license_tier?.toUpperCase()} · As Staff you can create customers, products,
            invoices, and reports.
          </div>
        </div>

        <form onSubmit={handleAccept} className="space-y-4" data-testid="invite-accept-form">
          <div className="space-y-1.5">
            <Label htmlFor="inv-name">Your full name</Label>
            <Input
              id="inv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="invite-accept-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inv-pwd">Choose a password</Label>
            <Input
              id="inv-pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              data-testid="invite-accept-password"
            />
            <p className="text-[10px] text-slate-500">At least 6 characters.</p>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="invite-accept-submit"
            className="w-full h-12 font-semibold text-white"
            style={{ backgroundColor: colors.dark }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Accept & join
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
